type ResolvedTheme = "dark" | "light";

function scalarThemeFromBody(body: HTMLElement): ResolvedTheme | null {
  if (body.classList.contains("dark-mode")) return "dark";
  if (body.classList.contains("light-mode")) return "light";
  return null;
}

/**
 * Keep Scalar's body-level color mode and the CMS html-level theme in sync.
 *
 * Scalar owns `body.dark-mode` / `body.light-mode` and persists `colorMode`;
 * the CMS owns `html.dark` / `html.light` and persists `cms-theme`. The CMS
 * wins on mount, while later Scalar-toggle mutations are reflected back into
 * the CMS through `setSiteTheme`.
 */
export function bridgeScalarTheme(
  siteIsDark: boolean,
  setSiteTheme: (theme: ResolvedTheme) => void,
  doc: Document = document,
): () => void {
  const body = doc.body;
  const siteRoot = doc.documentElement;
  const scalarTheme: ResolvedTheme = siteIsDark ? "dark" : "light";

  body.classList.toggle("dark-mode", siteIsDark);
  body.classList.toggle("light-mode", !siteIsDark);
  doc.defaultView?.localStorage.setItem("colorMode", scalarTheme);

  const Observer = doc.defaultView?.MutationObserver;
  if (!Observer) {
    return () => {
      body.classList.remove("dark-mode", "light-mode");
    };
  }

  const observer = new Observer(() => {
    const nextTheme = scalarThemeFromBody(body);
    if (!nextTheme) return;

    const siteMatches = siteRoot.classList.contains(nextTheme);
    if (!siteMatches) setSiteTheme(nextTheme);
  });

  observer.observe(body, {
    attributes: true,
    attributeFilter: ["class"],
  });

  return () => {
    observer.disconnect();
    body.classList.remove("dark-mode", "light-mode");
  };
}
